import { useEffect, useState, useTransition } from "react";
import { Link } from "react-router-dom";

import { IconPlus } from "@/components/Icons";
import {
  Button,
  Empty,
  Pill,
  SearchField,
  Segmented,
  Select,
  Spacer,
  Table,
  Td,
  Th,
  Toolbar,
} from "@/components/kit";
import type { FixturePage } from "@/lib/api/types";
import { displayName } from "@/lib/format";
import { SportGlyph } from "@/lib/sportIcons";

import styles from "./FixturesTable.module.css";

const PAGE_SIZE = 25;

type Status = "" | "SCHEDULED" | "LIVE" | "COMPLETED";

const STATUSES: { value: Status; label: string }[] = [
  { value: "", label: "All" },
  { value: "SCHEDULED", label: "Scheduled" },
  { value: "LIVE", label: "Live" },
  { value: "COMPLETED", label: "Finished" },
];

/**
 * How a status reads, and how loudly.
 *
 * Live is the only one that means somebody is waiting, so it is the only one with a
 * dot and a warm colour. Seven green COMPLETED pills shouting down a column told
 * nobody anything: the rows worth attention are the ones *not* finished, and they
 * were the quietest on the screen.
 */
const STATUS: Record<string, { label: string; tone: "live" | "good" | "warn" | "neutral" }> = {
  SCHEDULED: { label: "Scheduled", tone: "neutral" },
  LIVE: { label: "Live", tone: "live" },
  COMPLETED: { label: "Finished", tone: "good" },
  POSTPONED: { label: "Postponed", tone: "warn" },
  ABANDONED: { label: "Abandoned", tone: "warn" },
  CANCELLED: { label: "Cancelled", tone: "warn" },
};

export type SportChoice = { code: string; icon?: string | null };

type Props = {
  page: FixturePage;
  /** Offered in the sport filter. Empty when the table is already sport-scoped. */
  sports: SportChoice[];
  /** Fixed sport for a sport-scoped table; the filter is then hidden. */
  sport?: string;
  load: (query: {
    sport?: string;
    q?: string;
    status?: string;
    offset: number;
  }) => Promise<FixturePage>;
};

/** "27 Jul 2026, 19:00" → the date and the time apart, so a column of times lines
 *  up and the date is not buried in the middle of every row. */
function splitWhen(label: string | null | undefined): { date: string; time: string | null } {
  if (!label) return { date: "—", time: null };
  const at = label.lastIndexOf(", ");
  if (at < 0) return { date: label, time: null };
  return { date: label.slice(0, at), time: label.slice(at + 2) };
}

/**
 * Fixtures with search, a status filter and paging.
 *
 * All three happen on the server. Filtering a page of 50 in the browser looked the
 * same until a sport had a real season, at which point a list could come back empty
 * purely because that sport's fixtures were not among the newest 50 overall.
 *
 * Rewritten onto the kit, which it predated: a bare `<table>` and a stylesheet of
 * its own, so headings sat centred over left-aligned data, nothing had a width, and
 * every row carried a monospace code nobody reads next to an "Open" link beside a
 * name that was already a link.
 *
 * What the shape says now:
 *
 * * **The row is the link** — one target, so there is no "Open" column and no
 *   question about which of two blue things to click.
 * * **Where it sits goes under its name**, rather than a column repeating "Madison
 *   Square Garden, 6 June" on three consecutive rows.
 * * **The date and the time are separate lines.** A schedule is read down the time
 *   column, and that only works if the times line up.
 * * **Only Live shouts.**
 */
export function FixturesTable({ page: initial, sports, sport, load }: Props) {
  const [page, setPage] = useState(initial);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<Status>("");
  const [pickedSport, setPickedSport] = useState(sport ?? "");
  const [offset, setOffset] = useState(0);
  const [pending, startTransition] = useTransition();

  // Debounced so typing does not fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      startTransition(async () => {
        const next = await load({
          sport: sport ?? (pickedSport || undefined),
          q: query.trim() || undefined,
          status: status || undefined,
          offset,
        });
        setPage(next);
      });
    }, 250);
    return () => clearTimeout(timer);
  }, [query, status, pickedSport, offset, sport, load]);

  const shown = page.rows.length;
  const from = page.total === 0 ? 0 : offset + 1;
  const narrowed = query.trim() !== "" || status !== "" || (!sport && pickedSport !== "");
  const pages = Math.ceil(page.total / PAGE_SIZE);

  /** Narrowing while on page four leaves an empty page four, which reads as
   *  "nothing matched". The reset belongs to the act of narrowing. */
  const narrow = (change: () => void) => {
    change();
    setOffset(0);
  };

  return (
    <div className={styles.wrap}>
      <Toolbar>
        <SearchField
          value={query}
          placeholder="Search fixtures"
          aria-label="Search fixtures"
          onChange={(e) => narrow(() => setQuery(e.target.value))}
        />
        <Segmented
          name="Filter by status"
          value={status}
          options={STATUSES}
          onChange={(next) => narrow(() => setStatus(next))}
        />
        {!sport && sports.length > 0 && (
          <Select
            className={styles.sportFilter}
            aria-label="Filter by sport"
            value={pickedSport}
            onChange={(e) => narrow(() => setPickedSport(e.target.value))}
          >
            <option value="">Every sport</option>
            {sports.map((s) => (
              <option key={s.code} value={s.code}>
                {displayName(s.code)}
              </option>
            ))}
          </Select>
        )}
        <Spacer />
        {sport && (
          <Link to={`/${sport}/fixtures/new`} className={styles.primaryAction}>
            <IconPlus size={15} />
            Enter a fixture
          </Link>
        )}
      </Toolbar>

      {shown === 0 ? (
        <Empty
          title={narrowed ? "Nothing matches" : "No fixtures yet"}
          note={
            narrowed
              ? "Widen the search, or choose All."
              : "A fixture is one scheduled contest. Entering one asks only what its kind of fixture declared."
          }
          action={
            narrowed ? (
              <Button
                onClick={() =>
                  narrow(() => {
                    setQuery("");
                    setStatus("");
                    if (!sport) setPickedSport("");
                  })
                }
              >
                Clear
              </Button>
            ) : sport ? (
              <Link to={`/${sport}/fixtures/new`} className={styles.primaryAction}>
                Enter the first one
              </Link>
            ) : undefined
          }
        />
      ) : (
        <div className={pending ? styles.pending : undefined}>
          <Table>
            <thead>
              <tr>
                <Th>Fixture</Th>
                {!sport && <Th className={styles.colSport}>Sport</Th>}
                <Th className={styles.colWhen}>Starts</Th>
                <Th className={styles.colStatus}>Status</Th>
              </tr>
            </thead>
            <tbody>
              {page.rows.map((row) => {
                const when = splitWhen(row.startsLabel);
                const state = STATUS[row.status] ?? { label: row.status, tone: "neutral" as const };
                return (
                  <tr key={row.id}>
                    <Td>
                      {/* The whole cell is the link, and the only one in the row.
                          The code rides along as its title: it is what you paste
                          into a spreadsheet, not what you scan for. */}
                      <Link
                        to={`/fixtures/${row.id}`}
                        className={styles.fixture}
                        title={row.code}
                      >
                        <span className={styles.fixtureName}>{row.name ?? row.code}</span>
                        {row.competition && (
                          <span className={styles.fixtureWhere}>{row.competition}</span>
                        )}
                      </Link>
                    </Td>
                    {!sport && (
                      <Td>
                        <span className={styles.sportCell}>
                          <SportGlyph
                            id={sports.find((s) => s.code === row.sportCode)?.icon}
                            size={15}
                          />
                          {displayName(row.sportCode)}
                        </span>
                      </Td>
                    )}
                    <Td>
                      <span className={styles.when}>
                        <span className={styles.whenDate}>{when.date}</span>
                        {when.time && <span className={styles.whenTime}>{when.time}</span>}
                      </span>
                    </Td>
                    <Td>
                      <Pill tone={state.tone} dot={row.status === "LIVE"}>
                        {state.label}
                      </Pill>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {/* Only when there is a second page. A disabled Previous and Next under "ten
          of ten" is two controls that can never do anything. */}
      {pages > 1 && (
        <div className={styles.pager}>
          <span className={styles.pageInfo}>
            {from}–{offset + shown} of {page.total}
          </span>
          <Spacer />
          <Button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}>
            Previous
          </Button>
          <Button
            disabled={offset + shown >= page.total}
            onClick={() => setOffset(offset + PAGE_SIZE)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
